import {Observable, throwError} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {Login} from '../login/Login';
import {CommonService} from './commons.service';
import {ConfigService} from './config.service';

import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
  HttpUrlEncodingCodec,
} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {EventsEmitterService} from './emitter.service';
import {LogService} from './log.service';

export class CustomQueryEncoderHelper extends HttpUrlEncodingCodec {
  encodeKey(k: string): string {
    k = super.encodeKey(k);
    return k.replace(/\+/gi, '%2B');
  }
  encodeValue(v: string): string {
    v = super.encodeValue(v);
    return v.replace(/\+/gi, '%2B');
  }
}

export enum ROLES {
  CHECKER = 'ROLE_Back-Office_Onboarding_Checker',
  MAKER = 'ROLE_Back-Office_Onboarding_Maker',
  BACKOFFICE_ONBOARD_ADMIN = 'ROLE_BackOffice_Onboarding_Admin',
  INTERNAL_STAFF = 'ROLE_Back-Office_Onboarding_Enquiry',
}

@Injectable()
export class SecurityService extends CommonService {
  public static stopRequests: boolean = false;
  public pageSize: number = 20;

  constructor(
    http: HttpClient,
    configService: ConfigService,
    protected eventsEmitterService: EventsEmitterService,
    public router: Router,
    logger: LogService,
  ) {
    super(http, configService, logger);
    this.pageSize = this.configService.getPageSize();
  }

  public getPageSize() {
    return this.pageSize;
  }

  /**
   *  Refresh & Access Token Request (Login)
   */
  public sendTokenRequest(login: Login) {
    const serviceUrl = this.baseUrl + 'oauth/token';
    const data: HttpParams = new HttpParams({
      encoder: new CustomQueryEncoderHelper(),
    })
      .append('grant_type', login.grant_type)
      .append('credentials', 'true')
      .append('scope', 'write')
      .append('username', login.username)
      .append('password', login.password)
      .append('client_id', 'angular2-client')
      .append('client_secret', 'angular2');
    return this.http
      .post(serviceUrl, data.toString(), {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
      })
      .pipe(map(this.extractData), catchError(this.handleError));
  }
  public checkToken() {
    let serviceUrl = this.baseUrl + 'RoutesService/check';
    return this.request(serviceUrl).pipe(map(this.extractData), catchError(this.handleError));
  }

  public getAuthorities() {
    let serviceUrl = this.baseUrl + 'AuthorizationService/getAuthorities';
    return this.request(serviceUrl).pipe(map(this.extractData), catchError(this.handleError));
  }

  public getAuthorizedType() {
    let serviceUrl = this.baseUrl + 'AuthorizationService/getAuthorizedType';
    return this.request(serviceUrl).pipe(map(this.extractData), catchError(this.handleError));
  }
  public setUserRoles(roles: string[]) {
    localStorage.setItem('roles', JSON.stringify(roles));
  }

  public getUserRoles() {
    return JSON.parse(localStorage.getItem('roles'));
  }

  public hasRole(role: ROLES): boolean {
    let roles: string[] = JSON.parse(localStorage.getItem('roles')) || [];
    return roles.includes(role);
  }

  public isAuditor(): boolean {
    return this.hasRole(ROLES.CHECKER) || this.hasRole(ROLES.MAKER);
  }

  public isAdmin(): boolean {
    return this.hasRole(ROLES.BACKOFFICE_ONBOARD_ADMIN);
  }

  public isOtherStaff(): boolean {
    return this.hasRole(ROLES.INTERNAL_STAFF);
  }

  public canPerformAction(componentName: string, actionName: string): Observable<any> {
    let serviceUrl = this.baseUrl + 'AuthorizationService/canPerformAction?componentName=' + componentName + '&actionName=' + actionName;
    return this.request(serviceUrl).pipe(map(super.extractData), catchError(super.handleError));
  }

  /**
   * Check if debtor has reverse factoring
   */
  public isReverse() {
    const serviceUrl = this.baseUrl + 'AuthorizationService/isReverse';
    return this.request(serviceUrl).pipe(map(this.extractData), catchError(this.handleError));
  }

  /**
   *  Refresh the Access Token (on expiration)
   */
  public refreshTokenRequest(): Observable<Response> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };
    const credentials =
      'grant_type=refresh_token' +
      '&client_id=angular2-client' +
      '&client_secret=angular2' +
      '&refresh_token=' +
      this.getFromStorage('refresh_token') +
      '&access_token=' +
      this.getFromStorage('access_token');
    const serviceUrl = this.baseUrl + 'oauth/token';

    return this.http.post(serviceUrl, credentials, options).pipe(
      map((x: HttpResponse<any>) => this.saveData(x)),
      catchError(this.handleError),
    );
  }
  /**
   *  Revoke Tokens (on logout)
   */
  public revokeToken() {
    const options = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      }),
    };
    const serviceUrl = this.baseUrl + 'oauth/revoke-token';

    return this.http
      .get(serviceUrl, options)
      .pipe(catchError(this.handleError));
  }

  public revokeRefreshToken() {
    let params = new URLSearchParams();
    params.append('token', this.getFromStorage('refresh_token'));

    let options = {method: 'POST', body: params};
    let serviceUrl = this.baseUrl + 'oauth/revoke-refresh-token';

    return this.request(serviceUrl, options).pipe(      
      catchError(this.handleError)
    );
  }

  public goToLogin(paramName?: string, paramVal?: string): void {
    this.clearStorages();
    SecurityService.stopRequests = false;
    if (this.configService.getIsLogoutExternal()) {
      if (this.configService.getIsSsoEnabled()) {
        window.location.href = this.configService.getSsoUrl();
      } else {
        window.location.href = this.configService.getLogoutUrl();
      }
    } else {
      if (paramName && paramVal) {
        let params = {};
        params[paramName] = paramVal;
        if (this.configService.getIsSsoEnabled()) {
          window.location.href = this.configService.getSsoUrl();
        } else {
          this.router.navigate(['/login', params]);
        }
      } else {
        if (this.configService.getIsSsoEnabled()) {
          window.location.href = this.configService.getSsoUrl();
        } else {
          this.router.navigateByUrl('login');
        }
      }
    }
  }

  public navigateTo(menuId: string, paramName?: string[], paramVal?: string[]): void {
    SecurityService.stopRequests = false;
    if (paramName && paramVal) {
      let params = {};
      paramName.forEach((element, index) => {
        params[element] = paramVal[index];
      });
      this.logger.log('Navigate params: ' + JSON.stringify(params));
      this.router.navigate(['/menu/' + menuId, JSON.stringify(params)]);
    } else {
      this.router.navigateByUrl('menu/' + menuId);
    }
  }

  /**
   *  Logout Service for log history
   */
  public logoutLog() {
    let headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    this.setAuthorizationHeader(headers);
    let options = {headers: headers, method: 'POST'};
    let credentials = 'access_token=' + this.getFromStorage('access_token');
    let serviceUrl = this.baseUrl + 'LoginService/logout';

    return this.http
      .post(serviceUrl, credentials, options)
      .pipe(map(this.extractData), catchError(this.handleError));
  }

  /**
   * All logout requests
   *
   * @param expired
   */
  public logout(expired = false) {
    this.logoutLog().subscribe({
      next: (response) => {
        this.logger.log('The logout action was logged: ' + JSON.stringify(response));
      },
      error: (error) => {
        this.logger.log('Could not log the logout action: ' + JSON.stringify(error));
      },
    });
    this.revokeRefreshToken().subscribe({
      next: (response) => {
        this.logger.log('R-Token revoke: ' + JSON.stringify(response));

        this.revokeToken().subscribe({
          next: (response) => {
            this.logger.log('A-Token revoke: ' + JSON.stringify(response));
            if (expired) {
              this.goToLogin('expired', 'true');
            } else {
              this.goToLogin();
            }
          },
          error: (error) => {
            this.logger.log('A-Token revoke error: ' + JSON.stringify(error));
            if (expired) {
              this.goToLogin('expired', 'true');
            } else {
              this.goToLogin();
            }
          },
        });
      },
      error: (error) => {
        this.logger.log('R-Token revoke error: ' + JSON.stringify(error));
        if (expired) {
          this.goToLogin('expired', 'true');
        } else {
          this.goToLogin();
        }
      },
    });
  }

  public setAuthorizationHeader(headers: HttpHeaders) {
    if (headers) {
      // headers.append('Accept' , 'application/json');
      return headers
        .append('Authorization', 'Bearer ' + this.getFromStorage('access_token'))
        .append('Accept-Language', this.configService.getLocale().toString());
    }
    return new HttpHeaders()
      .append('Authorization', `Bearer ${this.getFromStorage('access_token')}`)
      .append('Accept-Language', this.configService.getLocale().toString());
  }
  // TODO: Interceptor for auth
  public request(url: string, options?: any): Observable<any> {
    let customOptions: any;

    if (!SecurityService.stopRequests) {
      if (options) {
        options.headers = this.setAuthorizationHeader(options.headers);
        return this.http.request(options.method ?? 'GET', url, options).pipe(
          catchError((initialError) => {
            this.logger.error(initialError);
            if (initialError && (initialError.status === 401 || initialError.status === 0)) {
              SecurityService.stopRequests = true;
              // token might be expired, try to refresh token.
              return (
                this.refreshTokenRequest()
                  //Use flatMap instead of map
                  .pipe(
                    mergeMap(() => {
                      //if (authenticationResult.IsAuthenticated == true) {
                      // retry with new token
                      this.logger.log('retry with new token');
                      options.headers = this.setAuthorizationHeader(options.headers);
                      SecurityService.stopRequests = false;
                      return this.http.request(url, options);
                      //}
                      //return throwError(() => new Error(initialError));
                    }),
                    catchError(() => {
                      this.goToLogin();
                      return throwError(() => new Error(initialError.error?.message ? initialError.error.message : initialError));
                    }),
                  )
              );
            } else {
              return throwError(() => new Error(initialError.error?.message ? initialError.error.message : initialError));
            }
          }),
        );
      } else {
        customOptions = {
          headers: this.setAuthorizationHeader(null),
        };
        return this.http.get(url, customOptions).pipe(
          catchError((initialError) => {
            this.logger.error(initialError);
            if (initialError && (initialError.status === 401 || initialError.status === 0)) {
              SecurityService.stopRequests = true;
              // token might be expired, try to refresh token.
              return (
                this.refreshTokenRequest()
                  //Use flatMap instead of map
                  .pipe(
                    mergeMap(() => {
                      //if (authenticationResult.IsAuthenticated == true) {
                      // retry with new token
                      this.logger.log('retry with new token');
                      customOptions.headers = this.setAuthorizationHeader(null);
                      SecurityService.stopRequests = false;
                      return this.http.request(url, customOptions);
                      //}
                      //return throwError(() => new Error(initialError));
                    }),
                    catchError(() => {
                      this.goToLogin();
                      return throwError(
                        () =>
                          new Error(
                            initialError.error?.message ? initialError.error.message : initialError,
                          ),
                      );
                    }),
                  )
              );
            } else {
              return throwError(
                () =>
                  new Error(
                    initialError.error?.message ? initialError.error.message : initialError,
                  ),
              );
            }
          }),
        );
      }
    } else {
      return throwError(() => new Error('Request not permitted!'));
    }
  }
}
